/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Grid,
  FormControl,
  Button,
  TextField,
  Autocomplete,
  TableFooter,
} from "@mui/material";
import Loader from '../../common/Loader';
import PageTitle from '../../common/PageTitle';
import LocalDateSelector from '../../common/LocalDateSelector';
import EmptyTable from '../../common/EmptyTable';
import { getAPI, postAPI } from '../../network';
import {
  checkAuthority,
  formatDatePost,
  getTimeFromTimestamp2,
  tableData,
  tableHeader
} from '../../utils';

export default function TenancyChecks() {
  const { siteId } = useParams();
  const [loader, setLoader] = useState(false);
  const [allTasks, setAllTasks] = useState([]); // Full dataset
  const [tasks, setTasks] = useState([]);       // Filtered dataset
  const [emailList, setEmailList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPageOptions] = useState([5, 10, 25, 50]);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    email: '',
  });

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const userList = [...new Map(
    emailList.map(item => [item.value, { id: item.value, name: item.label }])
  ).values()];

  const getTenancyList = async () => {
    setLoader(true);
    try {
      const data = await getAPI(`/tenancy-check?siteId=${siteId}`);
      if (data) {
        setAllTasks(data);
        setTasks(data); // initially unfiltered
        const emails = data.map(item => {
          const user = item?.shiftId?.assignedUser;
          return {
            label: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
            value: user?._id,
          };
        });
        setEmailList(emails);
      }
    } catch (error) {
      console.error('Failed to fetch tenancy data:', error);
    }
    setLoader(false);
  };

  const handleResend = async (tenancyId) => {
    try {
      setLoader(true);
      await postAPI(`/tenancy-check/resend-email`, { tenancyId });
    } catch (error) {
      console.error("Resend failed", error);
    } finally {
      setLoader(false);
    }
  };

  // Apply frontend filters
  useEffect(() => {
    let filtered = [...allTasks];

    if (filter.startDate) {
      const start = new Date(filter.startDate).setHours(0, 0, 0, 0);
      filtered = filtered.filter(item =>
        new Date(item.createdAt).setHours(0, 0, 0, 0) >= start
      );
    }

    if (filter.endDate) {
      const end = new Date(filter.endDate).setHours(23, 59, 59, 999);
      filtered = filtered.filter(item =>
        new Date(item.createdAt).setHours(0, 0, 0, 0) <= end
      );
    }

    if (filter.email) {
      filtered = filtered.filter(item =>
        item?.shiftId?.assignedUser?._id === filter.email
      );
    }

    setTasks(filtered);
    setPage(0); // reset pagination
  }, [filter, allTasks]);

  useEffect(() => {
    getTenancyList();
  }, []);

  return (
    <Box>
      <Loader loader={loader} />
      <PageTitle title="Tenancy Checks" />
      <Box ml={2}>
        <FormControl fullWidth>
          <Grid container spacing={2} my={2}>
            <Grid item xs={12} md={4} lg={3}>
              <LocalDateSelector
                title="Start Date"
                value={filter.startDate}
                onChange={(date) =>
                  setFilter(prev => ({ ...prev, startDate: date }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <LocalDateSelector
                title="End Date"
                value={filter.endDate}
                onChange={(date) =>
                  setFilter(prev => ({ ...prev, endDate: date }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <Autocomplete
                options={userList}
                getOptionLabel={(option) => option.name || ''}
                value={userList.find(u => u.id === filter.email) || null}
                onChange={(e, option) => {
                  setSelectedUser(option);
                  setFilter(prev => ({ ...prev, email: option?.id || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by User" variant="outlined" />
                )}
              />
            </Grid>
          </Grid>
        </FormControl>

        {checkAuthority('VIEW_TASKS') && (
          <Box my={3}>
            <TableContainer component={Paper}>
              <Table aria-label="tenancy checks table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeader}>Date Submitted</TableCell>
                    <TableCell sx={tableHeader}>Site</TableCell>
                    <TableCell sx={tableHeader}>Shift Type</TableCell>
                    <TableCell sx={tableHeader}>Resources Name</TableCell>
                    <TableCell sx={tableHeader}>Time Submitted</TableCell>
                    <TableCell sx={tableHeader}>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((task, index) => (
                      <TableRow key={index}>
                        <TableCell sx={tableData}>
                          {task?.createdAt?.split("T")[0]}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {task?.shiftId?.siteId?.name || '-'}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {task?.shiftId?.shiftType === 'Fixed' ? 'Rostered' : 'Casual'}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {`${task?.shiftId?.assignedUser?.firstname} ${task?.shiftId?.assignedUser?.lastname}`}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {getTimeFromTimestamp2(task?.updatedAt)}
                        </TableCell>
                        <TableCell sx={tableData}>
                          <Button
                            variant="contained"
                            onClick={() => handleResend(task._id)}
                          >
                            Resend
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  {tasks.length === 0 && (
                    <EmptyTable colSpan={6} message="No tenancy checks found." />
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={rowsPerPageOptions}
                      count={tasks.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
}
